 import React from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import { LogoLockup, LionMark } from "./Logo";
import { useLang } from "../i18n";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer id="contact" className="relative bg-ink-surface">
      {/* Get in touch block */}
      <div className="container-luxe section-pad relative overflow-hidden text-center">
        <LionMark
          size={540}
          className="pointer-events-none absolute left-1/2 top-1/2 max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.05]"
        />
        <div className="relative z-10">
          <Reveal>
            <p className="overline mb-5">{t.footer.contactTitle}</p>
            <h2 className="mx-auto max-w-3xl font-serif text-4xl font-light leading-tight text-white sm:text-5xl lg:text-6xl">
              {t.footer.tagline}
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base text-white/55 md:text-lg">{t.footer.contactText}</p>
          </Reveal>

        <Reveal delay={0.15}>
          <a
            data-testid="get-in-touch-btn"
            href={`mailto:${t.footer.email}`}
            className="mt-10 inline-flex items-center gap-3 bg-gold px-10 py-4 text-xs uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:bg-gold-light"
          >
            <Mail size={16} /> {t.footer.cta}
          </a>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-12 flex flex-col items-center justify-center gap-6 text-sm text-white/65 sm:flex-row sm:gap-12">
            <a data-testid="footer-email" href={`mailto:${t.footer.email}`} className="flex items-center gap-2 transition-colors hover:text-gold">
              <Mail size={16} className="text-gold" /> {t.footer.email}
            </a>
            <a data-testid="footer-phone" href={`tel:${t.footer.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 transition-colors hover:text-gold">
              <Phone size={16} className="text-gold" /> {t.footer.phone}
            </a>
            <span className="flex items-center gap-2">
              <MapPin size={16} className="text-gold" /> {t.footer.address}
            </span>
          </div>
        </Reveal>
        </div>
      </div>

      {/* Per-sector contact directory (from the Contact Us brief) */}
      <div className="border-t border-gold/10">
        <div className="container-luxe py-14">
          <Reveal>
            <p className="overline mb-8 text-center">{t.footer.deptsTitle}</p>
          </Reveal>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {t.footer.depts.map((d, i) => (
              <Reveal key={d.email} delay={(i % 3) * 0.06}>
                <a
                  data-testid={`dept-${i}`}
                  href={`mailto:${d.email}?subject=${encodeURIComponent(`${t.footer.mailSubject}${d.label}`)}`}
                  className="group flex h-full flex-col gap-1 border border-gold/12 bg-ink/80 backdrop-blur-xl px-7 py-6 transition-colors duration-400 hover:border-gold/35 hover:bg-ink-elevated"
                >
                  <span className="font-serif text-lg text-white/90 group-hover:text-gold-leaf">{d.label}</span>
                  <span className="text-xs tracking-wide text-white/45 group-hover:text-white/70">{d.email}</span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gold/15">
        <div className="container-luxe flex flex-col items-center justify-between gap-5 py-8 text-center md:flex-row md:gap-6 md:text-left">
          <LogoLockup markSize={38} />
          <p className="max-w-[16rem] text-[10px] uppercase leading-relaxed tracking-[0.18em] text-gold/70 sm:max-w-none sm:text-xs sm:tracking-[0.25em]">{t.footer.note}</p>
          <p className="text-xs text-white/35">© {year} Chespi Group. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
}
